import { GridData } from 'components/common-form/builder/form/node/field/Grid/GridField.model';
import { mapPastedRow } from 'components/common-form/builder/form/node/field/Grid/pasting';
import { useDateFormatDetector } from 'hooks/use-date-format-detector';
import { useSimplerUserNotification } from 'hooks/use-simple-user-notification';
import type { FieldType } from 'models/forms/fields/FieldMetadata';
import type { GridFieldHeader } from 'models/forms/fields/GridMetadata';
import { useCallback } from 'react';

import { useSPIntl } from './useIntl';

interface Grid {
  gridData: Readonly<GridData>;
  setGridData: (grid: Readonly<GridData>) => void;
  headers: readonly GridFieldHeader[];
}

interface Position {
  firstRow: number;

  lastRow: number;

  rowCount: number;

  firstColumn: number;

  lastColumn: number;

  columnCount: number;
}

function extractDateCandidate(cellValue: string, columnType: FieldType): string[] {
  switch (columnType) {
    case 'DateField':
      return [cellValue];
    default:
      return [];
  }
}

function getAllDatesCandidates(
  newValues: string[][],
  headers: readonly GridFieldHeader[],
  { rowCount, firstColumn, lastColumn }: Position,
): string[] {
  const result = newValues.slice(0, rowCount).flatMap(row => {
    return headers
      .slice(firstColumn, lastColumn + 1)
      .flatMap((c, colIncr) => extractDateCandidate(row[colIncr], c.type));
  });
  return result;
}

function getColumnCount<T>(grid: ReadonlyArray<ReadonlyArray<T>>): number {
  return grid.reduce((max, { length }) => Math.max(max, length), 0);
}

export function usePasteGridData({ gridData, headers, setGridData }: Grid) {
  const { formatMessage } = useSPIntl();
  const userNotifier = useSimplerUserNotification('toast.paste');
  const detectDateFormat = useDateFormatDetector()(userNotifier);

  return useCallback(
    (firstRow: number, firstColumn: number, newValues: string[][]) => {
      const lastRow = Math.min(firstRow + newValues.length - 1, gridData.length - 1);
      const lastColumn = Math.min(firstColumn + getColumnCount(newValues) - 1, headers.length - 1);

      const position: Position = {
        firstRow,
        lastRow,
        firstColumn,
        rowCount: lastRow - firstRow + 1,
        lastColumn,
        columnCount: lastColumn - firstColumn + 1,
      };

      const allDatesToParse = getAllDatesCandidates(newValues, headers, position);
      const detectedDateFormat = detectDateFormat(allDatesToParse);

      if (detectedDateFormat === null) {
        return;
      }

      const updatedRows = gridData.slice(firstRow, lastRow + 1).map((row, index) => {
        const pastedRow = newValues[index];
        const colMetadata = headers.slice(firstColumn, lastColumn + 1);
        const newRow = mapPastedRow(row, pastedRow, colMetadata, detectedDateFormat, formatMessage);

        return {
          ...newRow,
          isEdited: true,
        };
      });

      const data = [...gridData.slice(0, firstRow), ...updatedRows, ...gridData.slice(lastRow + 1)];

      const successMsg = formatMessage(
        {
          id: 'moreMenu.pasteFromClipboard.successGrid',
        },
        { rowCount: position.rowCount, columnCount: position.columnCount },
      );
      userNotifier.success(successMsg);

      setGridData(data);
    },
    [formatMessage, gridData, headers, setGridData, userNotifier, detectDateFormat],
  );
}
